/* eslint-disable react-hooks/exhaustive-deps */
import { People, Person, RemoveCircle, RemoveCircleDisable, UserIconFilled } from '@/components/icons'
import { LockedRating } from '@/components/profile-limit'
import { useOutsideClick } from '@/hooks/useOutsideClick'
import { useRatingLock } from '@/hooks/useRatingLock'
import { useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchPlayer } from './SearchPlayer'

type Props = {
  isMe?: boolean
  isSingle: boolean
  onChange?: (id: number) => void
  onChangeRemove?: () => void
  error?: string | undefined
  isClubMatchNew?: boolean
  isYourself?: boolean
  exclude?: number[]
  playerData?: any
}

export const Player = ({
  isMe,
  isSingle,
  onChange,
  onChangeRemove,
  error,
  isClubMatchNew,
  isYourself,
  exclude,
  playerData,
}: Props) => {
  const { t } = useTranslation()

  const { user } = useAppSelector(userSelector)

  const [openSearchPlayer, setOpenSearchPlayer] = useState(false)
  // Player | ClubMember
  const [player, setPlayer] = useState<any>(null)

  const isRatingLocked = useRatingLock()(player?.id)

  // Player | ClubMember
  const onSelect = (value: any) => {
    if (value) {
      setPlayer(value)
      setOpenSearchPlayer(false)
      if (onChange) onChange(value.id)
    }
  }

  const handleClickOutside = () => {
    setOpenSearchPlayer(false)
  }

  const handleRemove = () => {
    setPlayer(null)
    if (onChangeRemove) onChangeRemove()
  }

  const ref = useOutsideClick(handleClickOutside)

  useEffect(() => {
    if (isYourself)
      setPlayer({
        imageUrl: user?.imageUrl,
        fullName: user?.fullName,
        singles: user?.stats?.singles,
        doubles: user?.stats?.doubles,
      })
  }, [isYourself])

  useEffect(() => {
    if (playerData) setPlayer(playerData)
  }, [playerData])

  return (
    <>
      <div
        className={`flex items-center justify-center max-h-[72px]  ${error && 'border-boldOrange border rounded-2xl'}`}
      >
        {isMe ? (
          <div className={`justify-between bg-navy-900 bg-opacity-10 rounded-2xl p-3 xs:p-4 flex items-center w-full`}>
            <div className="flex items-center font-semibold">
              {user?.imageUrl ? (
                <img src={user.imageUrl} className="h-10 w-10 rounded-full" alt="User avatar" />
              ) : (
                <UserIconFilled className="w-10 h-10 text-navy-900" />
              )}
              <span className="text-xs xs:text-sm text-navy-900 ml-2">{user?.fullName}</span>
            </div>
            <div className="flex items-center">
              <div className="bg-gray-300 rounded-lg mr-1.5 xs:mr-2.5 flex flex-col justify-center items-center w-10 h-10">
                {isSingle ? <Person className="text-navy-900" /> : <People className="text-navy-900" />}
                <span className="mt-1 text-navy-900 text-xs font-medium">
                  {isSingle ? user?.stats?.singles : user?.stats?.doubles}
                </span>
              </div>
              <button disabled>
                <RemoveCircleDisable />
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`relative ${
              !player ? 'bg-opacity-10 cursor-pointer relative' : 'justify-between'
            } bg-navy-900 bg-opacity-10 rounded-2xl p-3 xs:p-4 flex items-center w-full`}
            onClick={() => {
              if (!player) setOpenSearchPlayer(true)
            }}
          >
            {!player && (
              <div className="flex items-center gap-x-2">
                <UserIconFilled className="w-10 h-10 text-navy-900 text-opacity-20" />
                <span className="text-base font-semibold text-navy-900 text-opacity-30">
                  {t('common.searchPlayer')}
                </span>
              </div>
            )}
            {player && (
              <>
                <div className="flex items-center font-semibold">
                  {player.imageUrl ? (
                    <img src={player.imageUrl} className="h-10 w-10 rounded-full" alt="User avatar" />
                  ) : (
                    <UserIconFilled className="w-10 h-10 text-navy-900" />
                  )}
                  <span className="text-xs xs:text-sm text-navy-900 ml-2">{player?.fullName}</span>
                </div>
                <div className="flex items-center">
                  <div className="bg-gray-300 rounded-lg mr-1.5 xs:mr-2.5 flex flex-col justify-center items-center w-10 h-10">
                    {isSingle ? <Person className="text-navy-900" /> : <People className="text-navy-900" />}
                    <span className="mt-1 text-navy-900 text-xs font-medium">
                      {isRatingLocked ? (
                        <LockedRating />
                      ) : isSingle ? (
                        isClubMatchNew ? (
                          player?.singles
                        ) : (
                          player?.ratings?.singles
                        )
                      ) : isClubMatchNew ? (
                        player?.doubles
                      ) : (
                        player?.ratings?.doubles
                      )}
                    </span>
                  </div>
                  <button onClick={handleRemove}>
                    <RemoveCircle />
                  </button>
                </div>
              </>
            )}
            {openSearchPlayer && (
              <div ref={ref} className="absolute w-full rounded-2xl top-0 left-0">
                <SearchPlayer
                  setOpenSearchPlayer={setOpenSearchPlayer}
                  onSelect={onSelect}
                  isClubMatchNew={isClubMatchNew}
                  exclude={exclude}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {error && <div className="text-boldOrange -mt-1 font-medium">{error}</div>}
    </>
  )
}
